import { useState } from 'react'
import { useApi } from '@/hooks/useApi'
import type { Proyecto, ReporteProgreso } from '@/lib/types'

interface Props {
  proyecto: Proyecto
}

type RangoPreset = 'hoy' | 'ayer' | 'semana' | 'mes' | 'custom'

function getRangoFechas(preset: RangoPreset): { desde: string; hasta: string } {
  const now = new Date()
  const fmt = (d: Date) => d.toISOString().split('T')[0]

  switch (preset) {
    case 'hoy': {
      return { desde: fmt(now), hasta: fmt(now) }
    }
    case 'ayer': {
      const ayer = new Date(now)
      ayer.setDate(ayer.getDate() - 1)
      return { desde: fmt(ayer), hasta: fmt(ayer) }
    }
    case 'semana': {
      const inicio = new Date(now)
      inicio.setDate(inicio.getDate() - 7)
      return { desde: fmt(inicio), hasta: fmt(now) }
    }
    case 'mes': {
      const inicio = new Date(now)
      inicio.setDate(inicio.getDate() - 30)
      return { desde: fmt(inicio), hasta: fmt(now) }
    }
    default:
      return { desde: fmt(now), hasta: fmt(now) }
  }
}

function formatPeriodo(desde: string, hasta: string): string {
  const dDesde = new Date(desde + 'T12:00:00')
  const dHasta = new Date(hasta + 'T12:00:00')
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }
  if (desde === hasta) {
    return dDesde.toLocaleDateString('es-PE', opts)
  }
  const optsShort: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' }
  return `${dDesde.toLocaleDateString('es-PE', optsShort)} — ${dHasta.toLocaleDateString('es-PE', opts)}`
}

export default function ReportesProyecto({ proyecto }: Props) {
  const api = useApi()

  const [preset, setPreset] = useState<RangoPreset>('semana')
  const [customDesde, setCustomDesde] = useState('')
  const [customHasta, setCustomHasta] = useState('')
  const [loading, setLoading] = useState(false)
  const [reporte, setReporte] = useState<ReporteProgreso | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  async function handleGenerar() {
    setLoading(true)
    setError(null)
    setReporte(null)

    try {
      const rango = preset === 'custom'
        ? { desde: customDesde, hasta: customHasta }
        : getRangoFechas(preset)

      if (!rango.desde || !rango.hasta) {
        setError('Selecciona un rango de fechas válido')
        return
      }

      const data = await api.post<ReporteProgreso>('/api/reportes/generar', {
        proyecto_id: proyecto.id,
        desde: rango.desde,
        hasta: rango.hasta,
      })
      setReporte(data)
    } catch (err: any) {
      console.error('Error generando reporte:', err)
      setError(err.message || 'Error al generar el reporte')
    } finally {
      setLoading(false)
    }
  }

  function buildPlainText(): string {
    if (!reporte) return ''
    const lines: string[] = []
    lines.push(`📊 Reporte de Progreso`)
    lines.push(`${reporte.proyecto}`)
    lines.push(`${formatPeriodo(reporte.periodo.desde, reporte.periodo.hasta)}`)
    lines.push(`Cliente: ${reporte.cliente}`)
    lines.push('')
    lines.push(reporte.resumen_ai)
    lines.push('')

    for (const sec of reporte.secciones) {
      lines.push(`${sec.emoji} ${sec.titulo}`)
      for (const item of sec.items) {
        lines.push(`  • ${item}`)
      }
      lines.push('')
    }

    if (reporte.estadisticas) {
      lines.push(`📈 Estadísticas`)
      lines.push(`  • ${reporte.estadisticas.total_commits} commits`)
      lines.push(`  • ${reporte.estadisticas.total_tareas_completadas} tareas completadas`)
      if (reporte.estadisticas.features > 0) lines.push(`  • ${reporte.estadisticas.features} funcionalidades nuevas`)
      if (reporte.estadisticas.fixes > 0) lines.push(`  • ${reporte.estadisticas.fixes} correcciones`)
    }

    return lines.join('\n')
  }

  async function handleCopy() {
    const text = buildPlainText()
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Empty state: no repo configured
  if (!proyecto.github_repo) {
    return (
      <div className="reporte-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.5 }}>
          <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
        </svg>
        <h3>Sin repositorio configurado</h3>
        <p>Para generar reportes de progreso, configura un repositorio GitHub en la edición del proyecto.</p>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          Formato: <code style={{ background: 'var(--veil-06)', padding: '2px 6px', borderRadius: 4 }}>owner/repo</code> — Ej: futura-wealth/platform
        </p>
      </div>
    )
  }

  return (
    <div className="reporte-container">
      {/* Controls */}
      <div className="reporte-controls">
        <div className="tags-filter-bar" style={{ marginBottom: 0 }}>
          <span className="tags-filter-label">Período:</span>
          {([
            { value: 'hoy', label: 'Hoy' },
            { value: 'ayer', label: 'Ayer' },
            { value: 'semana', label: 'Última semana' },
            { value: 'mes', label: 'Último mes' },
            { value: 'custom', label: 'Personalizado' },
          ] as { value: RangoPreset; label: string }[]).map(opt => (
            <button
              key={opt.value}
              className={`tag-chip ${preset === opt.value ? 'active' : ''}`}
              onClick={() => setPreset(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="date"
              className="form-input"
              value={customDesde}
              onChange={e => setCustomDesde(e.target.value)}
              style={{ width: 160, height: 34, fontSize: '0.82rem' }}
            />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <input
              type="date"
              className="form-input"
              value={customHasta}
              onChange={e => setCustomHasta(e.target.value)}
              style={{ width: 160, height: 34, fontSize: '0.82rem' }}
            />
          </div>
        )}

        <button
          className="btn btn-primary"
          onClick={handleGenerar}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {loading ? (
            <>
              <span className="spinner" style={{ width: 14, height: 14 }} /> Generando...
            </>
          ) : (
            <>📊 Generar Reporte</>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginTop: 16,
          background: 'color-mix(in srgb, var(--danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
          color: 'var(--danger)', fontSize: '0.85rem',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="reporte-preview" style={{ marginTop: 20 }}>
          <div className="reporte-header-skeleton">
            <div className="skeleton-bar" style={{ width: '60%', height: 20 }} />
            <div className="skeleton-bar" style={{ width: '40%', height: 14, marginTop: 8 }} />
          </div>
          <div style={{ padding: 24 }}>
            <div className="skeleton-bar" style={{ width: '100%', height: 14 }} />
            <div className="skeleton-bar" style={{ width: '90%', height: 14, marginTop: 8 }} />
            <div className="skeleton-bar" style={{ width: '70%', height: 14, marginTop: 8 }} />
            <div className="skeleton-bar" style={{ width: '50%', height: 20, marginTop: 24 }} />
            <div className="skeleton-bar" style={{ width: '85%', height: 14, marginTop: 12 }} />
            <div className="skeleton-bar" style={{ width: '80%', height: 14, marginTop: 8 }} />
            <div className="skeleton-bar" style={{ width: '75%', height: 14, marginTop: 8 }} />
          </div>
        </div>
      )}

      {/* Report preview */}
      {reporte && !loading && (
        <div className="reporte-preview" style={{ marginTop: 20 }}>
          {/* Header */}
          <div className="reporte-header">
            <div className="reporte-header-badge">📊 Reporte de Progreso</div>
            <h2 className="reporte-header-title">{reporte.proyecto}</h2>
            <p className="reporte-header-periodo">{formatPeriodo(reporte.periodo.desde, reporte.periodo.hasta)}</p>
            <p className="reporte-header-cliente">Cliente: {reporte.cliente}</p>
          </div>

          {/* Body */}
          <div className="reporte-body">
            {/* Resumen AI */}
            {reporte.resumen_ai && (
              <div className="reporte-resumen">
                <p>{reporte.resumen_ai}</p>
              </div>
            )}

            {/* Secciones */}
            {reporte.secciones.map((sec, i) => (
              <div key={i} className="reporte-seccion">
                <h3 className="reporte-seccion-title">
                  <span>{sec.emoji}</span> {sec.titulo}
                  <span className="reporte-seccion-count">({sec.items.length})</span>
                </h3>
                <ul className="reporte-seccion-list">
                  {sec.items.map((item, j) => (
                    <li key={j} className="reporte-seccion-item">{item}</li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Estadísticas */}
            {reporte.estadisticas && (
              <div className="reporte-stats">
                <h3 className="reporte-seccion-title"><span>📈</span> Estadísticas</h3>
                <div className="reporte-stats-grid">
                  <div className="reporte-stat-card">
                    <span className="reporte-stat-number">{reporte.estadisticas.total_commits}</span>
                    <span className="reporte-stat-label">Commits</span>
                  </div>
                  <div className="reporte-stat-card">
                    <span className="reporte-stat-number">{reporte.estadisticas.total_tareas_completadas}</span>
                    <span className="reporte-stat-label">Tareas</span>
                  </div>
                  {reporte.estadisticas.features > 0 && (
                    <div className="reporte-stat-card">
                      <span className="reporte-stat-number" style={{ color: 'var(--success)' }}>{reporte.estadisticas.features}</span>
                      <span className="reporte-stat-label">Features</span>
                    </div>
                  )}
                  {reporte.estadisticas.fixes > 0 && (
                    <div className="reporte-stat-card">
                      <span className="reporte-stat-number" style={{ color: 'var(--warning, #f39c12)' }}>{reporte.estadisticas.fixes}</span>
                      <span className="reporte-stat-label">Fixes</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tareas completadas del Kanban */}
            {reporte.tareas_completadas.length > 0 && (
              <div className="reporte-seccion">
                <h3 className="reporte-seccion-title"><span>✔️</span> Tareas completadas en Kanban</h3>
                <ul className="reporte-seccion-list">
                  {reporte.tareas_completadas.map((t, i) => (
                    <li key={i} className="reporte-seccion-item">
                      {t.titulo}
                      {t.horas_reales ? <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: '0.82rem' }}>({t.horas_reales}h)</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Commits raw (collapsible) */}
            {reporte.commits_raw.length > 0 && (
              <details className="reporte-seccion" style={{ cursor: 'pointer' }}>
                <summary className="reporte-seccion-title" style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🔍</span> Commits técnicos ({reporte.commits_raw.length})
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>click para expandir</span>
                </summary>
                <ul className="reporte-commits-list">
                  {reporte.commits_raw.map((c, i) => (
                    <li key={i} className="reporte-commit-item">
                      <code className="reporte-commit-sha">{c.sha}</code>
                      <span className="reporte-commit-msg">{c.message}</span>
                      <span className="reporte-commit-author">{c.author}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {/* Actions bar */}
          <div className="reporte-actions">
            <button className="btn btn-secondary" onClick={handleCopy} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {copied ? '✅ Copiado' : '📋 Copiar texto'}
            </button>
            <button className="btn btn-secondary" onClick={handleGenerar} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              🔄 Regenerar
            </button>
          </div>
        </div>
      )}

      {/* Repo info footer */}
      <div style={{ marginTop: 16, fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
        </svg>
        Repo: <a href={`https://github.com/${proyecto.github_repo}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>{proyecto.github_repo}</a>
      </div>
    </div>
  )
}
